import { User, UserX, ShieldCheck, Monitor } from "lucide-react";
import { AppColorVariant } from "../theme/AppColorVariant";
import {MultipleSelectOption} from "../components/common/GenericMultipleSelect";

export enum AuditLogType {
  ClientConfigurationChange = "clientConfigurationChange",
  OrganizationConfigurationChange = "organizationConfigurationChange",
  CasesExport = "casesExport",
  CaseFilesExport = "caseFilesExport",
  IntakesExport = "intakesExport",
  IntakeFilesExport = "intakeFilesExport",
  FirmDocumentsFoldersExport = "firmDocumentsFoldersExport",
  FirmDocumentsExport = "firmDocumentsExport",
  ProvidersExport = "providersExport",
  ProviderFilesExport = "providerFilesExport",
  TemplatesExport = "templatesExport",
  TemplateFilesExport = "templateFilesExport",
  OrganizationTemporaryAccessGrant = "organizationTemporaryAccessGrant",
  OrganizationAssignedUserChange = "organizationAssignedUserChange",
  DefaultClientConfigurationChange = "defaultClientConfigurationChange",
  ActiveClientConfigurationChange = "activeClientConfigurationChange",
  ClientConfigurationCreate = "clientConfigurationCreate",
  ClientConfigurationDelete = "clientConfigurationDelete",
  MarkNotFoundCaseFileForRetry = "markNotFoundCaseFileForRetry",
  MarkNotFoundFirmDocumentForRetry = "markNotFoundFirmDocumentForRetry",
  MarkNotFoundIntakeFileForRetry = "markNotFoundIntakeFileForRetry",
  MarkNotFoundProviderFileForRetry = "markNotFoundProviderFileForRetry",
  MarkNotFoundTemplateFileForRetry = "markNotFoundTemplateFileForRetry",
  MarkNotFoundFilesForRetry = "markNotFoundFilesForRetry",
  FlaggedPathChange = "flaggedPathChange",
  FlaggedPathsExport = "flaggedPathsExport",
  ClientSetToPendingUninstall = 'clientSetToPendingUninstall',
  CaseFlagUpdate = 'caseFlagUpdate',
  IntakeFlagUpdate = 'intakeFlagUpdate',
  CaseBlockUpdate = 'caseBlockUpdate',
  IntakeBlockUpdate = 'intakeBlockUpdate',
  ProviderBlockUpdate = 'providerBlockUpdate',
  FirmDocumentsFolderBlockUpdate = 'firmDocumentsFolderBlockUpdate',
  TemplatesLibraryBlockUpdate = 'templatesLibraryBlockUpdate',
  ClientCacheSettingsChange = "clientCacheSettingsChange",
  ClientForceRemoved = "clientForceRemoved",
  SqlScriptExecute = 'sqlScriptExecute',
  CaseFileConflictResolved = "caseFileConflictResolved",
  IntakeFileConflictResolved = "intakeFileConflictResolved",
  ProviderFileConflictResolved = "providerFileConflictResolved",
  TemplateFileConflictResolved = "templateFileConflictResolved",
  FirmDocumentConflictResolved = "firmDocumentConflictResolved",
  // One entry per org-wide bulk resolution, rather than one per file like the five types above.
  OrgWideConflictResolution = "orgWideConflictResolution",
  // One entry per attempt, in either direction, whether or not SharePoint accepted it.
  GraphLibraryVersioningChange = "graphLibraryVersioningChange",
  // One entry per upload, carrying the run's counts — not one per deleted record.
  BulkDeletionByUploadedIds = "bulkDeletionByUploadedIds",
  // One entry per request, not per client flagged. Records that a sweep was asked for; clients pick the
  // request up on their next configuration poll, so it never records that one ran.
  FolderSweepRequested = "folderSweepRequested",
  // One entry per export asked for and one per download of a finished one. AdditionalData carries the filter
  // the export ran under, never the rows it produced.
  SourceLinksExport = "sourceLinksExport",
  // A request to rebuild the upload order: Refresh Files Migration Queue, or a SQL script's follow-up refresh.
  FilesPriorityRecalculation = "filesPriorityRecalculation",
  // A portal user reading the organization's client-enrolment secret. No details: the row itself is the record.
  OrganizationSecretViewed = "organizationSecretViewed",
  // A Dropbox or Microsoft refresh token removed on Organization Configuration. AdditionalData names the account.
  RefreshTokenDeleted = "refreshTokenDeleted",
  ClientVersionBlacklistReset = "clientVersionBlacklistReset",
  ClientSoftDeleted = "clientSoftDeleted",
  ClientUninstallCancelled = "clientUninstallCancelled",
  // One entry per deletion queued from a Manage or Manage files drawer, however many targets it named.
  DeletionRequested = "deletionRequested",
  OrganizationDeleted = "organizationDeleted",
  ConsentLinkCreated = "consentLinkCreated",
  PathCollisionFlagsReconciled = "pathCollisionFlagsReconciled",
  // One entry per conflict rule run, however many groups it decided; the decided files and skipped groups are in
  // its drill-down, not in AdditionalData.
  ConflictRuleRun = "conflictRuleRun",
  // One entry per CSV downloaded of a run or of a preview.
  ConflictRuleRunExport = "conflictRuleRunExport"
}

export const AuditLogTypeLabels: Record<AuditLogType, string> = {
  [AuditLogType.ClientConfigurationChange]: "Client Configuration Change",
  [AuditLogType.OrganizationConfigurationChange]: "Organization Configuration Change",
  [AuditLogType.CasesExport]: "Cases Export",
  [AuditLogType.CaseFilesExport]: "Case Files Export",
  [AuditLogType.IntakesExport]: "Intakes Export",
  [AuditLogType.IntakeFilesExport]: "Intake Files Export",
  [AuditLogType.FirmDocumentsFoldersExport]: "Firm Documents Folders Export",
  [AuditLogType.FirmDocumentsExport]: "Firm Documents Export",
  [AuditLogType.ProvidersExport]: "Providers Export",
  [AuditLogType.ProviderFilesExport]: "Provider Files Export",
  [AuditLogType.TemplatesExport]: "Templates Export",
  [AuditLogType.TemplateFilesExport]: "Template Files Export",
  [AuditLogType.OrganizationTemporaryAccessGrant]: "Organization Temporary Access Grant",
  [AuditLogType.OrganizationAssignedUserChange]: "Organization Assigned User Change",
  [AuditLogType.DefaultClientConfigurationChange]: "Default Client Configuration Change",
  [AuditLogType.ActiveClientConfigurationChange]: "Active Client Configuration Change",
  [AuditLogType.ClientConfigurationCreate]: "Client Configuration Create",
  [AuditLogType.ClientConfigurationDelete]: "Client Configuration Delete",
  [AuditLogType.MarkNotFoundCaseFileForRetry]: "Mark Not Found Case File For Retry",
  [AuditLogType.MarkNotFoundFirmDocumentForRetry]: "Mark Not Found Firm Document For Retry",
  [AuditLogType.MarkNotFoundIntakeFileForRetry]: "Mark Not Found Intake File For Retry",
  [AuditLogType.MarkNotFoundProviderFileForRetry]: "Mark Not Found Provider File For Retry",
  [AuditLogType.MarkNotFoundTemplateFileForRetry]: "Mark Not Found Template File For Retry",
  [AuditLogType.MarkNotFoundFilesForRetry]: "Mark Not Found Files For Retry",
  [AuditLogType.FlaggedPathChange]: "Flagged Path Change",
  [AuditLogType.FlaggedPathsExport]: "Flagged Paths Export",
  [AuditLogType.ClientSetToPendingUninstall]: "Client Set To Pending Uninstall",
  [AuditLogType.CaseFlagUpdate]: "Case Flag Update",
  [AuditLogType.IntakeFlagUpdate]: "Intake Flag Update",
  [AuditLogType.CaseBlockUpdate]: "Case Block Update",
  [AuditLogType.IntakeBlockUpdate]: "Intake Block Update",
  [AuditLogType.ProviderBlockUpdate]: "Provider Block Update",
  [AuditLogType.FirmDocumentsFolderBlockUpdate]: "Firm Documents Folder Block Update",
  [AuditLogType.TemplatesLibraryBlockUpdate]: "Templates Library Block Update",
  [AuditLogType.ClientCacheSettingsChange]: "Client Cache Settings Change",
  [AuditLogType.ClientForceRemoved]: "Client Force Removed",
  [AuditLogType.SqlScriptExecute]: "SQL Script Execute",
  [AuditLogType.CaseFileConflictResolved]: "Case File Conflict Resolved",
  [AuditLogType.IntakeFileConflictResolved]: "Intake File Conflict Resolved",
  [AuditLogType.ProviderFileConflictResolved]: "Provider File Conflict Resolved",
  [AuditLogType.TemplateFileConflictResolved]: "Template File Conflict Resolved",
  [AuditLogType.FirmDocumentConflictResolved]: "Firm Document Conflict Resolved",
  [AuditLogType.OrgWideConflictResolution]: "Org-wide Conflict Resolution",
  [AuditLogType.GraphLibraryVersioningChange]: "Library Versioning Change",
  [AuditLogType.BulkDeletionByUploadedIds]: "Deletion From Uploaded List",
  [AuditLogType.FolderSweepRequested]: "Folder Sweep Requested",
  [AuditLogType.SourceLinksExport]: "Source Links Export",
  [AuditLogType.FilesPriorityRecalculation]: "Files Migration Queue Refresh",
  [AuditLogType.OrganizationSecretViewed]: "Organization Secret Viewed",
  [AuditLogType.RefreshTokenDeleted]: "Refresh Token Deleted",
  [AuditLogType.ClientVersionBlacklistReset]: "Client Version Blacklist Reset",
  [AuditLogType.ClientSoftDeleted]: "Client Soft Deleted",
  [AuditLogType.ClientUninstallCancelled]: "Client Uninstall Cancelled",
  [AuditLogType.DeletionRequested]: "Deletion Requested",
  [AuditLogType.OrganizationDeleted]: "Organization Deleted",
  [AuditLogType.ConsentLinkCreated]: "Consent Link Created",
  [AuditLogType.PathCollisionFlagsReconciled]: "Path Collision Flags Re-checked",
  [AuditLogType.ConflictRuleRun]: "Conflict Rule Run",
  [AuditLogType.ConflictRuleRunExport]: "Conflict Rule CSV Download"
};

/**
 * Label for an audit-log event type. Retired event types are still returned by the
 * API for historical rows (e.g. the removed file-download events), so anything the
 * UI no longer knows falls back to a humanized form of the raw camelCase value.
 */
export const getAuditLogTypeLabel = (eventType: string): string =>
  AuditLogTypeLabels[eventType as AuditLogType]
  ?? eventType.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase()).trim();

export enum AuditLogPerformerType {
  AssignedUser = "assignedUser",
  ExternalUser = "externalUser",
  Admin = "admin",
  Client = "client",
}

export const AuditLogPerformerTypeLabels: Record<AuditLogPerformerType, string> = {
  [AuditLogPerformerType.AssignedUser]: "Assigned User",
  [AuditLogPerformerType.ExternalUser]: "External User",
  [AuditLogPerformerType.Admin]: "Admin",
  [AuditLogPerformerType.Client]: "Client",
}

export const AuditLogPerformerTypeColors: Record<AuditLogPerformerType, AppColorVariant> = {
  [AuditLogPerformerType.AssignedUser]: "success",
  [AuditLogPerformerType.ExternalUser]: "danger",
  [AuditLogPerformerType.Admin]: "warning",
  [AuditLogPerformerType.Client]: "neutral",
};

export const AuditLogPerformerTypeIcons: Record<AuditLogPerformerType, React.ElementType> = {
  [AuditLogPerformerType.AssignedUser]: User,
  [AuditLogPerformerType.ExternalUser]: UserX,
  [AuditLogPerformerType.Admin]: ShieldCheck,
  [AuditLogPerformerType.Client]: Monitor,
};

export const AuditLogPerformerTypeMultipleSelectOptions: MultipleSelectOption[] = [
  { id: AuditLogPerformerType.AssignedUser, label: AuditLogPerformerTypeLabels[AuditLogPerformerType.AssignedUser] },
  { id: AuditLogPerformerType.ExternalUser, label: AuditLogPerformerTypeLabels[AuditLogPerformerType.ExternalUser] },
  { id: AuditLogPerformerType.Admin, label:  AuditLogPerformerTypeLabels[AuditLogPerformerType.Admin] },
  { id: AuditLogPerformerType.Client, label:  AuditLogPerformerTypeLabels[AuditLogPerformerType.Client] },
];

export interface AuditLog {
  id: string;
  entityId: string | null;
  eventType: AuditLogType;
  oldValue: string | null;
  newValue: string | null;
  additionalData: string | null;
  performedBy: string;
  eventDate: string;
  performerEmailAddress: string;
  performerType: AuditLogPerformerType
}

/**
 * Router state that opens Audit Logs filtered to some event types and, when `auditLogId` is among the first page of
 * entries, with that entry's details open. The page reads it once, when it opens.
 */
export interface OpenAuditLogState {
  eventTypes: AuditLogType[];
  auditLogId?: string;
}

export interface EventTypeGroup {
  label: string;
  types: { key: AuditLogType; label: string }[];
}

export const EVENT_TYPE_GROUPS: EventTypeGroup[] = [
  {
    label: "Cases",
    types: [
      { key: AuditLogType.CaseFlagUpdate, label: "Flag Updates" },
      { key: AuditLogType.CaseBlockUpdate, label: "Block Updates" },
      { key: AuditLogType.CasesExport, label: "Exports" },
      { key: AuditLogType.CaseFilesExport, label: "File Exports" },
    ],
  },
  {
    label: "Intakes",
    types: [
      { key: AuditLogType.IntakeFlagUpdate, label: "Flag Updates" },
      { key: AuditLogType.IntakeBlockUpdate, label: "Block Updates" },
      { key: AuditLogType.IntakesExport, label: "Exports" },
      { key: AuditLogType.IntakeFilesExport, label: "File Exports" },
    ],
  },
  {
    label: "Firm Documents",
    types: [
      { key: AuditLogType.FirmDocumentsFolderBlockUpdate, label: "Folder Block Updates" },
      { key: AuditLogType.FirmDocumentsFoldersExport, label: "Folder Exports" },
      { key: AuditLogType.FirmDocumentsExport, label: "Exports" },
    ],
  },
  {
    label: "Providers",
    types: [
      { key: AuditLogType.ProviderBlockUpdate, label: "Block Updates" },
      { key: AuditLogType.ProvidersExport, label: "Exports" },
      { key: AuditLogType.ProviderFilesExport, label: "File Exports" },
    ],
  },
  {
    label: "Templates",
    types: [
      { key: AuditLogType.TemplatesLibraryBlockUpdate, label: "Block Updates" },
      { key: AuditLogType.TemplatesExport, label: "Exports" },
      { key: AuditLogType.TemplateFilesExport, label: "File Exports" },
    ],
  },
  {
    label: "Client Configuration",
    types: [
      { key: AuditLogType.ClientConfigurationCreate, label: "Creations" },
      { key: AuditLogType.ClientConfigurationChange, label: "Changes" },
      { key: AuditLogType.ClientConfigurationDelete, label: "Deletions" },
      { key: AuditLogType.DefaultClientConfigurationChange, label: "Default Config Changes" },
      { key: AuditLogType.ActiveClientConfigurationChange, label: "Active Config Changes" },
      { key: AuditLogType.ClientCacheSettingsChange, label: "Cache Settings Changes" },
      { key: AuditLogType.ClientSetToPendingUninstall, label: "Pending Uninstall" },
      { key: AuditLogType.ClientForceRemoved, label: "Force Removed" },
      { key: AuditLogType.ClientSoftDeleted, label: "Soft Deleted" },
      { key: AuditLogType.ClientUninstallCancelled, label: "Uninstall Cancelled" },
      { key: AuditLogType.ClientVersionBlacklistReset, label: "Blacklist Resets" },
      // Grouped here because it is performed from a client configuration and scoped to its targets,
      // even though what it changes lives in SharePoint.
      { key: AuditLogType.GraphLibraryVersioningChange, label: "Library Versioning Changes" },
    ],
  },
  {
    label: "Organization",
    types: [
      { key: AuditLogType.OrganizationConfigurationChange, label: "Configuration Changes" },
      { key: AuditLogType.OrganizationAssignedUserChange, label: "Assigned User Changes" },
      { key: AuditLogType.OrganizationTemporaryAccessGrant, label: "Temporary Access Grants" },
      { key: AuditLogType.FlaggedPathChange, label: "Flagged Path Changes" },
      { key: AuditLogType.FlaggedPathsExport, label: "Flagged Paths Exports" },
      { key: AuditLogType.SourceLinksExport, label: "Source Links Exports" },
      { key: AuditLogType.OrganizationSecretViewed, label: "Secret Views" },
      { key: AuditLogType.RefreshTokenDeleted, label: "Refresh Token Deletions" },
      { key: AuditLogType.OrganizationDeleted, label: "Organization Deletions" },
      { key: AuditLogType.ConsentLinkCreated, label: "Consent Links" },
    ],
  },
  {
    label: "Not Found Retries",
    types: [
      { key: AuditLogType.MarkNotFoundCaseFileForRetry, label: "Case Files" },
      { key: AuditLogType.MarkNotFoundIntakeFileForRetry, label: "Intake Files" },
      { key: AuditLogType.MarkNotFoundProviderFileForRetry, label: "Provider Files" },
      { key: AuditLogType.MarkNotFoundTemplateFileForRetry, label: "Template Files" },
      { key: AuditLogType.MarkNotFoundFirmDocumentForRetry, label: "Firm Documents" },
      { key: AuditLogType.MarkNotFoundFilesForRetry, label: "All Files" },
    ],
  },
  {
    label: "Conflict Resolutions",
    types: [
      { key: AuditLogType.CaseFileConflictResolved, label: "Case Files" },
      { key: AuditLogType.IntakeFileConflictResolved, label: "Intake Files" },
      { key: AuditLogType.ProviderFileConflictResolved, label: "Provider Files" },
      { key: AuditLogType.TemplateFileConflictResolved, label: "Template Files" },
      { key: AuditLogType.FirmDocumentConflictResolved, label: "Firm Documents" },
      { key: AuditLogType.OrgWideConflictResolution, label: "Org-wide" },
      { key: AuditLogType.PathCollisionFlagsReconciled, label: "Re-checked by Client" },
      { key: AuditLogType.ConflictRuleRun, label: "Rule Runs" },
      { key: AuditLogType.ConflictRuleRunExport, label: "Rule CSV Downloads" },
    ],
  },
  {
    label: "Management",
    types: [
      { key: AuditLogType.SqlScriptExecute, label: "SQL Script Executions" },
      { key: AuditLogType.DeletionRequested, label: "Deletions From Manage" },
      { key: AuditLogType.BulkDeletionByUploadedIds, label: "Deletions From Uploaded Lists" },
      { key: AuditLogType.FolderSweepRequested, label: "Folder Sweep Requests" },
      { key: AuditLogType.FilesPriorityRecalculation, label: "Queue Refreshes" },
    ],
  },
];

export const ALL_EVENT_TYPES: AuditLogType[] = EVENT_TYPE_GROUPS.flatMap(g => g.types.map(t => t.key));
